/**
 * The surface's auth boundary — the single place that decides which of four
 * surfaces renders. Delegates the decision to the pure {@link authGateView}
 * (`auth-model.ts`) over the injected {@link SessionStore} (DIP):
 *   1. boot `restore()` still in flight (`!restored`) → a themed splash — an
 *      already signed-in reload must never flash the front door while the
 *      cookie round-trip is settling.
 *   2. the restore never reached the edge (`unreachable`) → {@link UnreachableNotice}.
 *      NOT the front door: a login form served against an edge that cannot answer
 *      fails too, and shows the raw transport string when it does.
 *   3. no session token → {@link AuthSurface} (login / sign-up).
 *   4. session but `!ageOk` → {@link AgeGate} (the wizard's age-verify step,
 *      also reached when a verified-nowhere user just logs in).
 *   5. both cleared → `children` (the companion surface).
 * `children` is lazy JSX — `<Switch>/<Match>` only resolve the winning branch's
 * children — so the product surface never mounts (no api calls, no `/ws`) until
 * the token is written AND the age gate is cleared.
 */
import { Button, Spinner, useT } from '@4f/ui';
import { type JSX, Match, Switch } from 'solid-js';
import type { SessionStore } from '../../stores/session.ts';
import { AgeGate } from './AgeGate.tsx';
import { AuthSurface } from './AuthSurface.tsx';
import { type AuthMode, authGateView } from './auth-model.ts';

export interface AuthGateProps {
  readonly session: SessionStore;
  /** An invite code from a referral link (`?ref=`), forwarded to sign-up. */
  readonly referralCode?: string;
  /**
   * Which front door the landing URL asked for (`/signup` vs `/login`), derived
   * once at boot. Undefined for any other path, which leaves the referral rule to
   * decide.
   */
  readonly initialMode?: AuthMode;
  /** The authenticated product surface — rendered only once both gates clear. */
  readonly children: JSX.Element;
}

/**
 * The boot-restore splash. Reuses the front door's own `f4-auth` full-screen
 * layout (already flex-centered) so it inherits the themed background with no
 * extra styling, and pulls its label from i18n like every other auth string.
 */
function RestoringSplash(): JSX.Element {
  const { t } = useT();
  return (
    <main class="f4-auth">
      <Spinner label={t('auth.restoring')} size="lg" />
    </main>
  );
}

/**
 * Shown when the boot restore could not reach the edge. Reuses `AgeGate`'s exact
 * `f4-auth__card` / `__title` / `__subtitle` / `__form` structure, so it inherits
 * the themed, centred layout with no new CSS — there is no bespoke styling in this
 * change at all.
 *
 * The retry re-runs `restore()` rather than reloading the page: the stored access
 * token is still held (that is the point of this state), and a reload would throw
 * away the in-memory session the next successful restore is about to adopt.
 */
function UnreachableNotice(props: { readonly session: SessionStore }): JSX.Element {
  const { t } = useT();
  return (
    <main class="f4-auth">
      <section class="f4-auth__card" aria-labelledby="unreachable-title" role="alert">
        <h1 id="unreachable-title" class="f4-auth__title">
          {t('auth.unreachable.title')}
        </h1>
        <p class="f4-auth__subtitle">{t('auth.unreachable.body')}</p>

        <div class="f4-auth__form">
          <Button
            onClick={() => void props.session.restore()}
            loading={props.session.loading()}
            loadingLabel={t('auth.loading')}
            fullWidth
          >
            {t('auth.unreachable.retry')}
          </Button>
        </div>
      </section>
    </main>
  );
}

export function AuthGate(props: AuthGateProps): JSX.Element {
  const view = () =>
    authGateView({
      restored: props.session.restored(),
      unreachable: props.session.unreachable(),
      authenticated: props.session.authenticated(),
      ageOk: props.session.ageOk(),
    });

  return (
    <Switch>
      <Match when={view() === 'restoring'}>
        <RestoringSplash />
      </Match>
      <Match when={view() === 'unreachable'}>
        <UnreachableNotice session={props.session} />
      </Match>
      <Match when={view() === 'front-door'}>
        <AuthSurface
          session={props.session}
          referralCode={props.referralCode}
          initialMode={props.initialMode}
        />
      </Match>
      <Match when={view() === 'age-gate'}>
        <AgeGate session={props.session} />
      </Match>
      <Match when={view() === 'surface'}>{props.children}</Match>
    </Switch>
  );
}
